import { useMemo, useState } from "react";
import {
  CollisionDetection,
  defaultDropAnimationSideEffects,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  DropAnimation,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import styled from "@emotion/styled";

import CentredSpinner from "lib/core_components/CentredSpinner";
import { SearchLeadsRequest } from "lib/generated/app-service-gql/graphql";
import { useLeads } from "lib/hooks/api/leads/useLeads";
import { useLeadWorkflow } from "lib/hooks/api/leads/useLeadWorkflow";
import { BOARD_COLUMN_PADDING_X, BOARD_COLUMN_WIDTH, BoardColumn } from "./BoardColumn";
import { CardShell, LeadCardBody } from "./LeadCard";
import { useLeadStageMove } from "./useLeadStageMove";
import { groupLeadsByStage, resolveStageMove, selectBoardStages } from "./utils";

const BOARD_PAGE_SIZE = 200;

const Scroller = styled.div({ overflowX: "auto", paddingBottom: 8 });
const Columns = styled.div({ display: "flex", gap: 12, alignItems: "flex-start" });
const Notice = styled.div(({ theme }) => ({
  padding: "8px 0",
  fontSize: 12,
  color: theme.colors.sysTextSecondary,
}));

// The pointer is the most accurate statement of intent, but pointerWithin finds
// nothing in the gutter between columns and nothing at all for keyboard drags,
// which have no pointer coordinates. Fall back to rectangle overlap in both cases.
const collideWithColumn: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  return pointerCollisions.length > 0 ? pointerCollisions : rectIntersection(args);
};

// Four layers rather than one: the tight top layer anchors the card to a contact
// edge while the wide bottom layer supplies the height. Each carries a negative
// spread scaled to its blur, which pulls the softer layers back under the card so
// they read as depth instead of bleeding out past its edges as haze.
const DRAG_SHADOW = [
  "0 1px 2px -1px rgba(38, 50, 71, 0.10)",
  "0 4px 8px -2px rgba(38, 50, 71, 0.10)",
  "0 12px 24px -6px rgba(38, 50, 71, 0.14)",
  "0 24px 48px -12px rgba(38, 50, 71, 0.12)",
].join(", ");

// The overlay is the card, lifted: same shell, column-width, and a shadow that also
// has to beat CardShell's hover shadow while the pointer sits on it. The tilt and
// the slight upscale are what sell it as picked up rather than merely sliding.
const DraggedCard = styled(CardShell)({
  width: BOARD_COLUMN_WIDTH - BOARD_COLUMN_PADDING_X * 2,
  cursor: "grabbing",
  transform: "rotate(3deg) scale(1.02)",
  boxShadow: DRAG_SHADOW,
  ":hover": { boxShadow: DRAG_SHADOW },
  "@media (prefers-reduced-motion: reduce)": { transform: "none" },
});

// Settle the overlay into place so the tilt unwinds on release. The source card is
// held at its ghost opacity for the duration, otherwise it flashes back to full
// strength the instant isDragging clears, while the overlay is still travelling.
const DROP_ANIMATION: DropAnimation = {
  duration: 200,
  easing: "cubic-bezier(0.2, 0, 0, 1)",
  sideEffects: defaultDropAnimationSideEffects({ styles: { active: { opacity: "0.4" } } }),
};

export function LeadsBoard({ request }: { request: SearchLeadsRequest }) {
  const [activeLeadId, setActiveLeadId] = useState<string | null>(null);

  // Only used for the stage list and its order/colours — no counts or sums.
  const { data: workflow, isError: isWorkflowError } = useLeadWorkflow();
  const {
    data,
    isLoading,
    isError: isLeadsError,
  } = useLeads(request, {
    page: 1,
    pageSize: BOARD_PAGE_SIZE,
  });
  const { overrides, moveLead } = useLeadStageMove();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const leads = useMemo(() => data?.results ?? [], [data]);
  const stages = useMemo(
    () => selectBoardStages(workflow, request.stageIds),
    [workflow, request.stageIds],
  );
  const columns = useMemo(
    () => groupLeadsByStage(leads, stages, overrides),
    [leads, stages, overrides],
  );
  const activeLead = activeLeadId ? leads.find((l) => l.id === activeLeadId) : undefined;

  const onDragStart = ({ active }: DragStartEvent) => setActiveLeadId(String(active.id));

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveLeadId(null);
    const move = resolveStageMove(active.id, over?.id, leads, overrides);
    if (move) moveLead(move.leadId, move.targetStageId);
  };

  // Both failures are silent without this. A failed workflow never resolves `workflow`,
  // so the `!workflow` guard below would hold the spinner forever; a failed leads query
  // renders every column as "No leads", which reads as an empty pipeline, not an error.
  if (isWorkflowError || isLeadsError) {
    return <Notice>Something went wrong loading the board. Refresh the page to try again.</Notice>;
  }
  if (isLoading || !workflow) return <CentredSpinner label="Loading leads board" />;
  // Reachable only when the workflow itself has no stages — an empty stage filter
  // means "no filter", so every stage still gets a column.
  if (stages.length === 0) {
    return (
      <Notice>
        This workflow has no stages yet. Add one in your workflow settings to see the board.
      </Notice>
    );
  }

  return (
    <div>
      <DndContext
        sensors={sensors}
        collisionDetection={collideWithColumn}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={() => setActiveLeadId(null)}
      >
        <Scroller>
          <Columns>
            {columns.map((column) => (
              <BoardColumn key={column.stage.id} column={column} />
            ))}
          </Columns>
        </Scroller>
        <DragOverlay dropAnimation={DROP_ANIMATION}>
          {activeLead ? (
            <DraggedCard>
              <LeadCardBody lead={activeLead} />
            </DraggedCard>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
